import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Suspense, useCallback, useEffect, useState} from 'react'

import {useActiveRoute} from '../../hooks/use-active-route'
import {useFileTreeDiffSummaries} from '../../page-data/loaders/use-file-tree-diff-summaries'
import {useInfiniteThreadsPageData} from '../../page-data/loaders/use-threads'
import {useLayoutStoreSeeded} from '../../stores/layout-selectors'
import {usePullRequestBasePath} from '../../stores/selectors'

/**
 * Renders nothing but lazily fetches file tree and thread data into the store
 * when rendered. Fetches the file tree (non-suspending) and all thread pages
 * (suspending). Must be wrapped in a Suspense boundary for the thread fetch.
 */
function LazyDataLoader({onLoaded}: {onLoaded: () => void}) {
  const basePath = usePullRequestBasePath()

  const {isFetched: isFileTreeFetched} = useFileTreeDiffSummaries({basePath, enabled: true})

  const {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isFetched: areThreadsFetched,
  } = useInfiniteThreadsPageData({
    pathName: basePath,
    hasMorePages: true,
  })

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    if (isFileTreeFetched && areThreadsFetched) {
      onLoaded()
    }
  }, [isFileTreeFetched, areThreadsFetched, onLoaded])

  return null
}

/** Isolates the lazy comment data fetch so a failure never takes down the comments UI. */
export function LazyCommentsDataLoader({onLoaded}: {onLoaded: () => void}) {
  return (
    <ErrorBoundary boundaryName="LazyDataLoader" fallback={null}>
      <Suspense fallback={null}>
        <LazyDataLoader onLoaded={onLoaded} />
      </Suspense>
    </ErrorBoundary>
  )
}

export interface LazyCommentsData {
  /** Whether `LazyCommentsDataLoader` should be rendered to fetch the missing data. */
  shouldLazyLoadData: boolean
  /** Whether comments are still being lazily fetched and a skeleton should be shown. */
  isLazyLoading: boolean
  onLazyDataLoaded: () => void
}

/**
 * Determines whether comment data has to be fetched on demand. Routes other than files
 * changed do not seed threads into the store up front, so the data is fetched the first
 * time comments are shown.
 */
export function useLazyCommentsData(): LazyCommentsData {
  const activeRoute = useActiveRoute()
  const isFilesChanged = activeRoute === 'files_changed'
  const layoutStoreSeeded = useLayoutStoreSeeded()

  const [lazyDataLoaded, setLazyDataLoaded] = useState(false)
  const onLazyDataLoaded = useCallback(() => setLazyDataLoaded(true), [])

  const shouldLazyLoadData = !isFilesChanged && isFeatureEnabled('updated_pull_request_header')

  return {
    shouldLazyLoadData,
    isLazyLoading: shouldLazyLoadData && !lazyDataLoaded && !layoutStoreSeeded,
    onLazyDataLoaded,
  }
}
